'use client';

import { usePathname } from 'next/navigation';
import Link from 'next/link';
import { useMemo, useEffect, useState } from 'react';
import { Home, ShoppingBag, ShoppingCart, Building2, Phone, User, ShieldCheck, BookOpen, Store } from 'lucide-react';
import { SignedIn, useAuth } from '@clerk/nextjs';
import { useUser } from '../../hooks/useUser';
import { UserRole } from '@/lib/roles-client';

export default function MobileNavbar() {
  const pathname = usePathname();
  const { isAdmin, user } = useUser();
  const { isSignedIn } = useAuth();
  const [isSmallScreen, setIsSmallScreen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Helper to check if a path is active
  const isActivePath = useMemo(() => {
    // Extract the path without locale prefix
    const currentPath = pathname || '/';
    
    // Return a function to check if a given path matches the current one
    return (path: string) => {
      return currentPath === path || currentPath === `${path}/` || currentPath.startsWith(`${path}/`);
    };
  }, [pathname]);
  
  // Core menu items with icons for better UX
  // Note: Online store ("/productos") hidden until further notice.
  const coreMenuItems = [
    {
      path: '/',
      label: 'Inicio',
      icon: Home
    },
    {
      path: '/cotizar',
      label: 'Cotizar',
      icon: ShoppingBag
    },
    {
      path: '/empresa',
      label: 'Empresa',
      icon: Building2
    },
    {
      path: '/blog',
      label: 'Blog',
      icon: BookOpen
    }
  ];
  
  // User-specific menu items - will be included if user is signed in
  const userMenuItem = {
    path: '/perfil',
    label: 'Perfil',
    icon: User
  };
  
  // Admin menu item - will be included if user is admin
  const adminMenuItem = {
    path: '/admin',
    label: 'Admin',
    icon: ShieldCheck
  };
  
  // Revendedor menu item - will be included if user is revendedor
  const revendedorMenuItem = {
    path: '/revendedores',
    label: 'Revendedores',
    icon: Store
  };

  // Check screen size to adjust UI accordingly
  useEffect(() => {
    const checkScreenSize = () => {
      // Update mobile status (smaller than medium breakpoint)
      setIsMobile(window.innerWidth < 768);
      
      // Consider it a very small screen if below 360px wide
      setIsSmallScreen(window.innerWidth < 360);
    };
    
    // Initial check
    checkScreenSize();
    
    // Check on resize
    window.addEventListener('resize', checkScreenSize);
    
    // Force repaint if needed
    const mobileNav = document.querySelector('.mobile-nav-container');
    if (mobileNav) {
      mobileNav.classList.add('force-visible');
    }
    
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);
  
  // Create final menu items array
  const getFinalMenuItems = () => {
    let items = [...coreMenuItems];
    
    // Add user and role-specific items if needed
    if (isSignedIn) {
      items.push(userMenuItem);
      
      if (isAdmin) {
        items.push(adminMenuItem);
      }
      
      if (user?.role === UserRole.REVENDEDOR) {
        items.push(revendedorMenuItem);
      }
    }
    
    // Remove empresa link on very small screens with many icons
    if (isSmallScreen && (isAdmin || user?.role === UserRole.REVENDEDOR) && isSignedIn) {
      items = items.filter(item => item.path !== '/empresa');
    }
    
    return items;
  };

  // Don't render anything if not on mobile
  if (!isMobile) return null;

  // Get all menu items to display
  const allMenuItems = getFinalMenuItems();

  return (
    <div 
      className="mobile-nav-container block md:hidden fixed bottom-0 left-0 right-0 z-[9999] bg-white border-t border-gray-200 shadow-lg pb-safe" 
      style={{ 
        zIndex: 9999,
        position: 'fixed'
      }}
    >
      <nav className="flex justify-around w-full">
        <ul className={`flex w-full justify-evenly items-center ${isSmallScreen ? 'h-14 px-1' : 'h-16'}`}>
          {allMenuItems.map((item) => {
            const Icon = item.icon;
            const isActive = isActivePath(item.path);
            
            return (
              <li key={item.path} className="flex-1 text-center">
                <Link
                  href={item.path}
                  className={`flex flex-col items-center justify-center h-full py-1 ${
                    isActive
                      ? 'text-[#a30000] font-medium'
                      : 'text-gray-500 hover:text-[#a30000]'
                  }`}
                  aria-current={isActive ? 'page' : undefined}
                >
                  <div className="relative">
                    <Icon className={`${isSmallScreen ? 'h-5 w-5' : 'h-6 w-6'} mb-0.5 ${isActive ? 'text-[#a30000]' : 'text-gray-500'}`} aria-hidden="true" />
                    {isActive && (
                      <span className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-1.5 h-1.5 bg-[#a30000] rounded-full"></span>
                    )}
                  </div>
                  <span className={`${isSmallScreen ? 'text-2xs' : 'text-xs'}`}>{item.label}</span>
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>
    </div>
  );
} 